import axios from 'axios';
import { PRIMARY_API_URL } from '../config/api';
// Re-export our local auth client. Importers (BookingModal, BookingsManagement,
// FleetService, VanSchedulerService) all import { supabase } from here but
// never call methods on it — pure legacy artifact. Keeping the export prevents
// breaking the import statements without forcing edits in 4 places.
export { default as supabase } from '../config/supabase';

const API_URL = `${PRIMARY_API_URL}/api`;

/**
 * Client-side service to interact with the Supabase-backed API
 */
class SupabaseService {
  // Van-related methods
  async getAllVans() {
    try {
      const response = await axios.get(`${API_URL}/fleet/vans`);
      return response.data.map(van => ({
        ...van,
        licensePlate: van.license_plate
      }));
    } catch (error) {
      console.error('Error fetching vans:', error);
      throw error;
    }
  }

  async getVan(vanId) {
    try {
      const response = await axios.get(`${API_URL}/fleet/vans/${vanId}`);
      const van = response.data;
      return {
        ...van,
        licensePlate: van.license_plate
      };
    } catch (error) {
      console.error(`Error fetching van ${vanId}:`, error);
      throw error;
    }
  }

  async updateVan(id, updates) {
    try {
      const response = await axios.put(`${API_URL}/fleet/vans/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error(`Error updating van ${id}:`, error);
      throw error;
    }
  }

  async createVan(vanData) {
    try {
      const response = await axios.post(`${API_URL}/fleet/vans`, vanData);
      return response.data;
    } catch (error) {
      console.error('Error creating van:', error);
      throw error;
    }
  }

  async checkVanAvailability(startTime, endTime, passengers) {
    // Format the request payload
    const requestPayload = {
      start_time: startTime,
      end_time: endTime,
      passengers: parseInt(passengers) || 1
    };
    
    // Validate inputs before proceeding
    if (!startTime || !endTime) {
      console.error('Invalid parameters for van availability check:', { startTime, endTime, passengers });
      throw new Error('Missing required parameters for availability check');
    }
    
    try {
      console.log('Checking van availability with params:', requestPayload);
      
      // Add a small delay to ensure the request doesn't race too quickly
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Make the API request with timeout to avoid hanging
      const response = await axios.post(`${API_URL}/fleet/vans/check-availability`, requestPayload, {
        timeout: 8000, // 8 second timeout
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Validate the response data
      const data = response.data;
      if (!data || typeof data !== 'object') {
        console.error('Invalid response format from availability API:', data);
        throw new Error('Invalid response format from server');
      }
      
      // Log the successful response for debugging
      console.log('Van availability response:', data);
      
      // Return sanitized response with defaults for missing fields
      return {
        available_vans: Array.isArray(data.available_vans) ? data.available_vans : [],
        total_capacity: data.total_capacity || 0,
        sufficient_capacity: !!data.sufficient_capacity,
        vans_needed: data.vans_needed || Math.ceil((parseInt(passengers) || 1) / 7),
        vans_available: !!data.vans_available,
        user_message: data.user_message || null,
        start_time: data.start_time || startTime,
        end_time: data.end_time || endTime
      };
    } catch (error) {
      // Enhanced error handling with detailed logs
      console.error('Error checking van availability:');
      
      if (error.response) {
        // The server responded with an error status code
        console.error('Server error response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
        
        // Provide a better error message based on the status code
        if (error.response.status === 404) {
          throw new Error('Availability service endpoint not found');
        } else if (error.response.status === 500) {
          throw new Error('Server error while checking availability');
        } else {
          throw new Error(`Error ${error.response.status}: ${error.response.data?.error || 'Unknown error'}`);
        }
      } else if (error.request) {
        // The request was made but no response was received (network error)
        console.error('Network error - no response received:', error.request);
        throw new Error('Network error: Failed to reach the availability service');
      } else {
        // Something happened in setting up the request
        console.error('Request setup error:', error.message);
        throw new Error(`Request failed: ${error.message}`);
      }
    }
  }

  // Booking assignment methods
  async assignBooking(bookingId, vanId) {
    try {
      const response = await axios.post(`${API_URL}/fleet/assignments`, {
        booking_id: bookingId,
        van_id: vanId
      });
      return response.data.assignment;
    } catch (error) {
      console.error('Error assigning booking:', error);
      throw error.response?.data?.error || error.message;
    }
  }

  async getBookingAssignments(bookingId) {
    try {
      const response = await axios.get(`${API_URL}/fleet/assignments/booking/${bookingId}`);
      return response.data.map(assignment => {
        return {
          ...assignment,
          vanName: assignment.van_name || (assignment.van ? assignment.van.name : '')
        };
      });
    } catch (error) {
      console.error(`Error fetching assignments for booking ${bookingId}:`, error);
      return [];
    }
  }

  async getVanAssignments(vanId) {
    try {
      const response = await axios.get(`${API_URL}/fleet/assignments/van/${vanId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching assignments for van ${vanId}:`, error);
      return [];
    }
  }

  async unassignBooking(bookingId) {
    try {
      const response = await axios.delete(`${API_URL}/fleet/assignments/booking/${bookingId}`);
      return response.data;
    } catch (error) {
      console.error(`Error unassigning booking ${bookingId}:`, error);
      throw error.response?.data?.error || error.message;
    }
  }

  // Van availability block methods
  async createVanBlock(vanId, startTime, endTime, reason) {
    try {
      const response = await axios.post(`${API_URL}/fleet/vans/blocks`, {
        van_id: vanId,
        start_time: startTime,
        end_time: endTime,
        reason
      });
      return response.data;
    } catch (error) {
      console.error('Error creating van block:', error);
      throw error.response?.data?.error || error.message;
    }
  }
  
  async getVanBlocks(vanId) {
    try {
      const response = await axios.get(`${API_URL}/fleet/vans/${vanId}/blocks`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching blocks for van ${vanId}:`, error);
      return [];
    }
  }
  
  async deleteVanBlock(blockId) {
    try {
      const response = await axios.delete(`${API_URL}/fleet/vans/blocks/${blockId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting block ${blockId}:`, error);
      throw error.response?.data?.error || error.message;
    }
  }

  // Update an assignment (without driver functionality)
  async updateAssignment(assignmentId, updates) {
    try {
      console.log(`Updating assignment ${assignmentId} with:`, updates);
      
      // Remove any driver-related fields
      const sanitizedUpdates = { ...updates };
      if ('driver_id' in sanitizedUpdates) delete sanitizedUpdates.driver_id;
      if ('driver_name' in sanitizedUpdates) delete sanitizedUpdates.driver_name;
      
      const response = await axios.put(`${API_URL}/fleet/assignments/${assignmentId}`, sanitizedUpdates, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (response.status >= 200 && response.status < 300) {
        console.log('Assignment updated successfully:', response.data);
        return response.data;
      } else {
        console.error('Unexpected response status:', response.status);
        throw new Error(`Failed to update assignment: ${response.status}`);
      }
    } catch (error) {
      console.error('Error updating assignment:', error);
      
      // Extract detailed error message if available
      if (error.response && error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error);
      }
      
      throw error;
    }
  }
}

export default new SupabaseService(); 